import React, { useContext } from "react"
import { useStateMachine } from "../../../context/stateMachine/StateMachineV2"
import { defineMessage, useIntl } from "react-intl"
import { Typography } from "@carrotfertility/carotene-core"
import { FormSelectOption } from "@carrotfertility/carotene-core-x"
import { CarrotCardCostSharePreAuthContext } from "#/components/carrot-card-cost-share/state/CarrotCardCostSharePreAuthContext"
import { useConvertTranslatedLabel } from "#/services/common-forms/useConvertTranslatedLabel"
import { WhoWillReceiveCareValues } from "#/components/carrot-card-cost-share/steps/shared/stepOptionValues"
import { SelectWithTileStep } from "#/components/carrot-card-cost-share/steps/variants/SelectWithTileStep"
import { ThisIsADifferentExpenseTypeBox } from "#/components/carrot-card-cost-share/steps/shared/ThisIsADifferentExpenseTypeBox"
import { JourneyTypes } from "#/components/get-reimbursed/steps/ExpenseJourneyTypes"

const recipientOptions = [
  { label: defineMessage({ defaultMessage: "Me" }), value: WhoWillReceiveCareValues.ME },
  {
    label: defineMessage({ defaultMessage: "My legal spouse or tax-dependent partner" }),
    value: WhoWillReceiveCareValues.LEGAL_PARTNER
  },
  {
    label: defineMessage({ defaultMessage: "My non-married partner" }),
    value: WhoWillReceiveCareValues.NON_MARRIED_PARTNER
  },
  {
    label: defineMessage({ defaultMessage: "Gestational carrier" }),
    value: WhoWillReceiveCareValues.GESTATIONAL_CARRIER
  },
  { label: defineMessage({ defaultMessage: "Donor" }), value: WhoWillReceiveCareValues.DONOR }
]

export function WhoWillReceiveCareFertilityStep(): JSX.Element {
  const intl = useIntl()
  const { send } = useStateMachine()
  const {
    dispatchFormState,
    formState: { whoReceivedCare },
    serverState: { hasDonorAssistanceCoverage, hasGestationalCarrierCoverage, checkPartnerEligibility }
  } = useContext(CarrotCardCostSharePreAuthContext)
  const formattedOptions = useConvertTranslatedLabel(recipientOptions) as FormSelectOption[]

  const onSubmit = (formValues: { [key: string]: string }): void => {
    dispatchFormState({ type: "UPDATE_FIELD", field: "whoReceivedCare", value: formValues.whoReceivedCare })
    const partnerEligibility = checkPartnerEligibility(formValues.whoReceivedCare)

    if (partnerEligibility === "INELIGIBLE") {
      send("INELIGIBLE")
      return
    }

    if (formValues.whoReceivedCare === WhoWillReceiveCareValues.DONOR && !hasDonorAssistanceCoverage) {
      send("INELIGIBLE")
      return
    }

    if (formValues.whoReceivedCare === WhoWillReceiveCareValues.GESTATIONAL_CARRIER && !hasGestationalCarrierCoverage) {
      send("GESTATIONAL_CARRIER_WITHOUT_COVERAGE")
      return
    }
    if (formValues.whoReceivedCare === WhoWillReceiveCareValues.GESTATIONAL_CARRIER && hasGestationalCarrierCoverage) {
      dispatchFormState({ type: "UPDATE_FIELD", field: "journeyType", value: JourneyTypes.GC })
    }

    send(formValues.whoReceivedCare)
  }

  return (
    <SelectWithTileStep
      title={intl.formatMessage({ defaultMessage: "Who will receive the care or service?" })}
      description={
        <Typography>
          {intl.formatMessage({
            defaultMessage:
              "If you have eligible expenses for more than one person, these need to be authorized as separate transactions."
          })}
        </Typography>
      }
      selectName="whoReceivedCare"
      options={formattedOptions}
      onSubmit={onSubmit}
      defaultValue={whoReceivedCare}
    >
      <ThisIsADifferentExpenseTypeBox name="whoReceivedCare" />
    </SelectWithTileStep>
  )
}
